/**
 * 영업진행 메뉴 — 유지보수 큰 탭 (Phase 6-4 보강).
 *
 * 상단:
 *   - 서브 탭 (계약현황 / 계산서별 발행일 / 월별 발행합계)
 *   - 연도 셀렉트
 *   - 📥 엑셀 다운로드 버튼 (현재 연도, 두 시트 .xlsx)
 *
 * 계약현황 탭 / 계산서별 발행일 탭:
 *   - 같은 표(ContractTable)의 두 모드(dateView). 상단 탭으로 전환.
 *   - 계약현황: DataTable 정렬·필터 (영업진행 동일 패턴). 행 클릭 → MaintenanceContractModal.
 *   - 계산서별 발행일: 계약 × 1~12월분 계산서 발행 '날짜' 표시.
 *   - 표 위 통합검색창(공용 SearchBar · v2.271.0) — 영업진행·매입관리와 같은 모양·동작(Enter·[검색]으로 적용).
 *     두 모드에 같이 걸린다. 맨 앞 「No」 칸은 v2.271.0 에 없앴다(머리글 정렬로 대신).
 *
 * 월별 발행합계 탭:
 *   - 계약 × 12개월 발행 '금액' 매트릭스 + 월별/연간 합계 (발행일 기준 집계)
 */

import { lazy, Suspense, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as analysisApi from '../lib/analysis-api';
import * as maintApi from '../lib/maintenance-api';
import * as prefsApi from '../lib/preferences-api';
import { syncAllData } from '../lib/invalidate';
import { MaintenanceBudgetBasisTab } from './MaintenanceBudgetBasisTab';
import { ResetButton } from './ResetButton';
import { SearchBar } from './SearchBar';
import { ExcelModeGroup } from './ExcelModeButton';
import { filterMaintRows, maintSearchSuggestions } from '../lib/maint-search';
import { useAuth } from '../lib/auth-context';
import { useExcelModeTable } from '../lib/useExcelModeTable';
import { PageTabs } from './ui/PageTabs';
import { previewAttachment } from '../lib/attachment-api';
import type {
  MaintenanceContractRow,
  MaintenanceMonthlyRow,
  MaintenancePrevCarryRow,
  MaintenanceStatus,
  UserPreferences,
} from '../lib/types';
import {
  type Column,
  ColumnHeader,
  applyColState,
  useColState,
} from './DataTable';
import { LoadingLines } from './ui/Skeleton';

type MaintenanceColumn = Column<MaintenanceContractRow> & {
  defaultOn: boolean;
  toggleLabel?: string;
};

// 모달은 lazy — 초기 번들에서 분리. 행 클릭 / ✎ 수정 시 처음 로드.
const MaintenanceContractModal = lazy(() =>
  import('./MaintenanceContractModal').then((m) => ({ default: m.MaintenanceContractModal })),
);
// 신규 매출 등록 모달 — [+ 유지보수 매출 등록] 클릭 시 처음 로드 (2026-05-17 매출분석에서 이전).
const MaintenanceSalesQuickModal = lazy(() =>
  import('./MaintenanceSalesQuickModal').then((m) => ({ default: m.MaintenanceSalesQuickModal })),
);
// 세금계산서 발행요청 — [📨 발행요청] 클릭 시 처음 로드 (v2.261.0).
const MaintenanceInvoiceRequestModal = lazy(() =>
  import('./MaintenanceInvoiceRequestModal').then((m) => ({ default: m.MaintenanceInvoiceRequestModal })),
);
// 신규 계약 등록 — [+ 계약 등록] 클릭 시 처음 로드.
const MaintenanceContractFormModal = lazy(() =>
  import('./MaintenanceContractFormModal').then((m) => ({ default: m.MaintenanceContractFormModal })),
);

type SubTab = 'contract' | 'dates' | 'monthly' | 'budget';

function fmtKrw(n: number): string {
  return n.toLocaleString('ko-KR');
}

/**
 * 조회 전용(viewer)에게 가려진 금액 (v2.251.0 · 사용자 「유지보수 금액만 블라인드」) — 서버가 null 로 보낸다.
 *   null = 가림 → 「***」 · 0 = 없음 → 「·」 · 그 밖 = 금액.
 */
const MASK = '***';
function money(n: number | null | undefined): string {
  if (n == null) return MASK;
  return n > 0 ? fmtKrw(n) : '·';
}

/** 'YYYY-MM-DD' → 'MM/DD' (월별 발행일자 칸용). 없으면 빈 문자열. */
function fmtMd(d: string | null | undefined): string {
  if (!d) return '';
  const p = d.split('-');
  return p.length >= 3 ? `${p[1]}/${p[2]}` : d;
}

const STATUS_CLASS: Record<MaintenanceStatus, string> = {
  확정: 'bg-emerald-100 text-emerald-700',
  미정: 'bg-amber-100 text-amber-700',
  취소: 'bg-slate-100 text-slate-600',
  종료: 'bg-slate-200 text-slate-700',
};

const STATUS_RANK: Record<MaintenanceStatus, number> = {
  확정: 1,
  미정: 2,
  종료: 3,
  취소: 4,
};

// 표는 table-layout:fixed 로 렌더 → 아래 지정 너비가 그대로 적용(내용에 따라 안 늘어남).
//   덕분에 계약현황 ↔ 월별 발행일자 전환 시 왼쪽 3칸(상태·고객사·청구업체)이 픽셀 단위로 안 움직임.

// 왼쪽 3개 컬럼 고정 너비 — 두 모드 공통. (계약현황 기존 모습에 근접)
//   ⚠맨 앞 「No」(44px · 금액 큰 순 번호)는 v2.271.0 에 없앴다 — 영업진행·매입관리처럼 머리글 정렬로 대신한다.
const LEFT_FIXED_WIDTH: Record<string, number> = {
  status: 64,
  endUser: 100,
  contractCompany: 124,
};

// 계약현황(일반) 모드 — 전 컬럼 고정 너비.
//   ※ 좁으면 한글 헤더가 글자단위로 세로로 쪼개져 헤더가 높아짐 → 라벨 들어갈 만큼은 확보.
const DEFAULT_COL_WIDTH: Record<string, number> = {
  ...LEFT_FIXED_WIDTH,
  awardCompany: 124,
  companyId: 68,
  pmName: 64,
  engineerName: 76,
  prevYearAmount: 88,
  currentYearAmount: 88,
  invoicedAmount: 96,
  subcontractAmount: 84,
  // 계약기간·계산서발행약정·비고 = 내용이 길어 넉넉히(길면 2줄 줄바꿈 허용, 잘림 없음).
  period: 178,
  billingCycle: 76,
  invoiceSchedule: 210,
  note: 320,
};

// 📅 월별 발행일자 모드 — 계약현황과 동일한 컬럼 폭을 그대로 쓰되(비고·금액3종 빠짐),
//   '계산서 발행정보'(contract_name) + 1~12월분 발행일 칸을 붙인다.
//   공유 컬럼 폭이 같아야 전환 시 왼쪽 칸이 안 움직임.
const DATE_COL_WIDTH: Record<string, number> = {
  ...DEFAULT_COL_WIDTH,
  invoiceInfo: 220,
  ...Object.fromEntries(Array.from({ length: 12 }, (_, i) => [`m${i + 1}`, 70])),
};

// 컬럼 폭 fallback(맵에 없을 때) — table-fixed 에서 0 방지.
const FALLBACK_COL_WIDTH_PX = 100;

interface OpenContract {
  id: number;
  /** 모달 마운트 시 즉시 수정 폼으로 진입 (인라인 ✎ 버튼에서 옴). */
  autoEdit?: boolean;
}

export function MaintenanceTab() {
  const { user } = useAuth();
  const canRegister = user?.role === 'sales' || user?.role === 'mgmt' || user?.role === 'admin';
  // 조회 전용(viewer) — v2.251.0 부터 이 화면을 본다. 금액은 서버가 가려 보내고(null), 엑셀·예산 산정근거·쓰기는 없다.
  const isViewer = user?.role === 'viewer';
  const [year, setYear] = useState(new Date().getFullYear());
  const [sub, setSub] = useState<SubTab>('contract');
  const [openContract, setOpenContract] = useState<OpenContract | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  // 신규 매출 등록 모달 (2026-05-17 매출분석에서 이전).
  const [salesQuick, setSalesQuick] = useState(false);
  // 신규 계약 등록 모달
  const [contractCreate, setContractCreate] = useState(false);
  // 세금계산서 발행요청 창 (v2.261.0)
  const [invoiceRequest, setInvoiceRequest] = useState(false);
  // 통합검색어 (v2.271.0) — Enter·[검색]으로 확정된 글. 계약현황·계산서별 발행일 두 보기에 같이 걸리고,
  //   여기(부모)에 두어 연도를 바꾸거나 월별 발행합계를 다녀와도 남는다(영업진행도 단계·연도를 바꿔도 검색어가 남는다).
  const [search, setSearch] = useState('');

  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ['analysis', 'maintenance', year],
    queryFn: () => analysisApi.getMaintenance(year),
    staleTime: 60_000,
  });
  const contracts = q.data?.contracts;
  const searchSuggestions = useMemo(() => maintSearchSuggestions(contracts ?? []), [contracts]);

  const createContractMut = useMutation({
    mutationFn: (input: Parameters<typeof maintApi.createContract>[0]) =>
      maintApi.createContract(input),
    onSuccess: () => {
      syncAllData(qc);
      setContractCreate(false);
    },
  });

  async function onExport() {
    try {
      setExporting(true);
      setExportError(null);
      await maintApi.downloadMaintenanceExcel(year);
    } catch (e) {
      setExportError((e as Error).message);
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="space-y-3">
      {/* 헤더 — 세부 탭(공용 PageTabs · v2.252.0) + 오른쪽에 기준 표시·연도·엑셀·등록
          예산 산정근거는 **sales 이상만** (v2.79.7 사용자 08-21).
          ⚠ 이 숨김은 거들 뿐이다 — 실제 차단은 서버 라우트 가드가 한다
            (budget-basis.ts 의 `requireRole(['sales','admin'])` · 28개 라우트 전부).
            viewer 가 주소를 직접 쳐도 API 가 403 이라 자료는 나가지 않는다.
            화면만 숨기고 서버를 안 막으면 08-05 견적서 사고가 되풀이된다. */}
      <PageTabs
        tabs={[
          { key: 'contract', label: '계약현황', count: q.data?.contracts.length },
          { key: 'dates', label: '계산서별 발행일' },
          { key: 'monthly', label: '월별 발행합계' },
          { key: 'budget', label: '📐 예산 산정근거 작성', hidden: !canRegister },
        ]}
        value={sub}
        onChange={setSub}
        ariaLabel="유지보수 보기"
        className="mb-0"
        right={
          <>
            {/* 기준 시각·부가세 안내·조회 전용 안내는 화면 머리(MaintenancePage)로 — 여기 두면 1366 폭에서 탭 줄이 접힌다(10-14 실측) */}
            {/* 연도 — 예산 산정근거는 누적(전 연도) 기준이라 숨긴다 (있으면 오해를 준다) */}
            {sub !== 'budget' && (
              <>
                <label className="ui-label">연도</label>
                <select
                  value={year}
                  onChange={(e) => setYear(Number(e.target.value))}
                  className="ui-input"
                >
                  {[year + 1, year, year - 1, year - 2].map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </>
            )}
            {/* 계약현황 엑셀 — 예산 산정근거 탭에서는 다른 자료라 숨긴다 (오해 방지)
                조회 전용(viewer)은 숨긴다 — 엑셀은 금액 그대로라 서버도 sales 이상으로 막는다(v2.251.0) */}
            {sub !== 'budget' && !isViewer && (
              <button
                type="button"
                onClick={onExport}
                disabled={exporting || q.isLoading}
                className="btn btn-secondary"
                title="현재 연도의 계약현황 + 월별 발행 두 시트로 엑셀 다운로드"
              >
                {exporting ? '내보내는 중…' : '📥 엑셀'}
              </button>
            )}
            {/* 등록 버튼 두 개는 **계약현황 탭에서만** (사용자 지시 2026-08-10).
                다른 탭에서는 그 탭에서 할 수 있는 일이 아니라 오히려 헷갈린다.
                ⚠ 연도·엑셀은 그대로 둔다 — 계산서별 발행일·월별 발행합계에서도 쓰인다. */}
            {canRegister && sub === 'contract' && (
              <button
                type="button"
                onClick={() => setContractCreate(true)}
                className="btn btn-secondary"
                title="신규 유지보수 계약 등록"
              >
                + 계약 등록
              </button>
            )}
            {/* 발행요청(v2.261.0) — 경영지원팀에 「이 계산서 발행해 주세요」 · 발행이 끝나면 옆 [+ 계산서 발행]으로 등록 */}
            {canRegister && sub === 'contract' && (
              <button
                type="button"
                onClick={() => setInvoiceRequest(true)}
                className="btn btn-outline"
                title="경영지원팀 웍스 방으로 유지보수 세금계산서 발행요청을 보냅니다 (영업건 발행요청과 같은 방)"
              >
                📨 발행요청
              </button>
            )}
            {canRegister && sub === 'contract' && (
              <button
                type="button"
                onClick={() => setSalesQuick(true)}
                className="btn btn-primary"
                title="계산서 발행 등록 (계약 선택 후 발행 월·금액 입력)"
              >
                + 계산서 발행
              </button>
            )}
          </>
        }
      />

      {exportError && (
        <div className="text-sm bg-rose-50 border border-rose-200 text-rose-700 rounded px-3 py-2">
          엑셀 다운로드 실패: {exportError}
        </div>
      )}

      {sub !== 'budget' && q.isLoading && (
        <div className="bg-white rounded border border-slate-200 p-12 text-center text-slate-400"><LoadingLines /></div>
      )}
      {sub !== 'budget' && q.isError && (
        <div className="bg-white rounded border border-rose-200 p-6 text-center text-rose-600">
          오류: {(q.error as Error).message}
        </div>
      )}

      {/* 예산 산정근거 — 계약현황과 다른 자료(엔드유저별 납품 집계)라 별도 조회 */}
      {sub === 'budget' && <MaintenanceBudgetBasisTab />}
      {/* 통합검색 (v2.271.0) — 영업진행·매입관리와 같은 공용 검색창·같은 카드 모양.
          고객사·청구업체·확약/낙찰업체·계약명·비고·입찰공고 건명 등을 한 번에 (lib/maint-search.ts).
          월별 발행합계는 다른 자료(발행일 기준 · 지난 계약 포함)라 걸지 않고 창도 숨긴다. */}
      {q.data && (sub === 'contract' || sub === 'dates') && (
        <div className="bg-white rounded shadow-sm border border-slate-200 p-4">
          <SearchBar
            value={search}
            onCommit={setSearch}
            placeholder="🔍 고객사 / 청구업체 / 확약·낙찰업체 / 계약명 / 비고 / 입찰공고 건명"
            suggestions={searchSuggestions}
          />
        </div>
      )}
      {/* 계약현황 ↔ 계산서별 발행일 은 같은 표(ContractTable)의 두 모드 —
          탭 전환해도 인스턴스를 유지해 헤더/행 높이 동기화(heightsRef)가 이어지게 함. */}
      {q.data && (sub === 'contract' || sub === 'dates') && (
        <ContractTable
          rows={q.data.contracts}
          year={year}
          dateView={sub === 'dates'}
          search={search}
          onClearSearch={() => setSearch('')}
          onOpen={(id) => setOpenContract({ id })}
        />
      )}
      {q.data && sub === 'monthly' && (
        <MonthlyMatrix
          rows={q.data.monthly}
          carry={q.data.prevCarryover}
          year={year}
          onOpen={(id) => setOpenContract({ id })}
        />
      )}

      {/* lazy 모달 — 처음 열 때 동적 로드 */}
      <Suspense fallback={null}>
        {openContract !== null && (
          <MaintenanceContractModal
            contractId={openContract.id}
            year={year}
            autoEdit={openContract.autoEdit}
            onClose={() => setOpenContract(null)}
          />
        )}
        {salesQuick && (
          <MaintenanceSalesQuickModal
            onClose={() => setSalesQuick(false)}
            onSaved={() => setSalesQuick(false)}
          />
        )}
        {invoiceRequest && (
          <MaintenanceInvoiceRequestModal onClose={() => setInvoiceRequest(false)} />
        )}
        {contractCreate && (
          <MaintenanceContractFormModal
            onClose={() => setContractCreate(false)}
            onSaveCreate={(input) => createContractMut.mutate(input)}
            saving={createContractMut.isPending}
            error={createContractMut.error ? (createContractMut.error as Error).message : null}
          />
        )}
      </Suspense>
    </div>
  );
}


// ─── 계약현황 표 (DataTable 사용) ───────────────────────

function ContractTable({
  rows,
  year,
  dateView,
  search,
  onClearSearch,
  onOpen,
}: {
  rows: MaintenanceContractRow[];
  year: number;
  // 상단 탭에서 결정 — false=계약현황, true=계산서별 발행일(상태·고객사·청구업체·사업자·발행금액 + 1~12월분 발행일).
  dateView: boolean;
  /** 표 위 통합검색어(확정된 글 · v2.271.0) — 두 보기에 같이 건다 */
  search: string;
  /** [↺ 필터·정렬 초기화] 가 검색어도 같이 비운다(영업진행·매입관리와 같이) */
  onClearSearch: () => void;
  onOpen: (contractId: number) => void;
}) {
  const colState = useColState();
  const qc = useQueryClient();
  const { user } = useAuth();
  const canEdit = user?.role === 'sales' || user?.role === 'mgmt' || user?.role === 'admin';
  // 조회 전용 — 금액 칸은 서버가 null 로 보낸다(「***」). 정렬·🔍 는 안 붙인다(전부 같은 값이라 뜻이 없다).
  //   ⭐다른 칸으로 정렬하면 값이 같은 줄끼리는 서버가 준 순서(viewer = 이름 순)로 남는다 → 금액 순서가 새지 않는다.
  const isViewer = user?.role === 'viewer';
  const maskedCol = isViewer ? { noSort: true, noFilter: true } : {};
  // 계약현황 뷰의 실제 헤더/행 높이를 측정 보관 → 월별 발행일자 뷰에서 같은 높이로 적용(빨간 박스 세로 고정).
  const tableRef = useRef<HTMLTableElement>(null);
  const heightsRef = useRef<{ header: number | null; rows: Map<number, number> }>({
    header: null,
    rows: new Map(),
  });

  // 오늘(YYYY-MM-DD) — 계약 종료일 만료 판정용
  const todayStr = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  })();
  const isExpired = (r: MaintenanceContractRow) =>
    r.status === '확정' && !!r.contractEnd && r.contractEnd < todayStr;

  const fullColumns = useMemo<MaintenanceColumn[]>(
    () => [
      {
        key: 'status', label: '상태', type: 'text', align: 'center',
        nowrapHeader: true,
        // 종료일이 지난 '확정' 계약은 '만료' 한 가지로만 표시 (2026-07-21 사용자 요청).
        //   저장값(확정)은 그대로 두고 화면 표시만 계산 → 정렬·컬럼필터도 표시값('만료') 기준.
        //   기존 '확정 + ⚠만료 + [종료]버튼' 3중 표시를 단일 배지로 정리.
        accessor: (r) => (isExpired(r) ? '만료' : r.status),
        sortAccessor: (r) => (isExpired(r) ? 0 : (STATUS_RANK[r.status] ?? 99)),
        render: (r) => {
          const expired = isExpired(r);
          const cls = expired ? 'bg-red-100 text-red-700' : STATUS_CLASS[r.status];
          return (
            <span
              className={`text-xs px-2 py-0.5 rounded ${cls}`}
              title={expired ? `계약 종료일 ${r.contractEnd} 경과` : undefined}
            >
              {expired ? '만료' : r.status}
            </span>
          );
        },
        defaultOn: true,
      },
      {
        key: 'endUser', label: '고객사 (엔드유저)', type: 'text', align: 'left',
        accessor: (r) => r.endUser,
        render: (r) => <span className="font-medium">{r.endUser}</span>,
        defaultOn: true,
      },
      {
        key: 'contractCompany', label: '청구업체', type: 'text', align: 'left',
        accessor: (r) => r.contractCompany,
        render: (r) => <span className="text-slate-600">{r.contractCompany}</span>,
        defaultOn: true,
      },
      {
        key: 'awardCompany', label: '확약/낙찰업체', type: 'text', align: 'left',
        accessor: (r) => r.awardCompany ?? '',
        render: (r) => <span className="text-xs text-slate-500">{r.awardCompany ?? '-'}</span>,
        defaultOn: false,
      },
      {
        key: 'companyId', label: '사업자', type: 'text', align: 'center',
        accessor: (r) => r.companyId,
        render: (r) => <span className="text-xs text-slate-600">{r.companyId}</span>,
        defaultOn: true,
      },
      {
        key: 'pmName', label: 'PM', type: 'text', align: 'left',
        accessor: (r) => r.pmName ?? '',
        render: (r) => <span className="text-xs">{r.pmName ?? '-'}</span>,
        defaultOn: false,
      },
      {
        key: 'engineerName', label: '엔지니어', type: 'text', align: 'left',
        accessor: (r) => r.engineerName ?? '',
        render: (r) => <span className="text-xs">{r.engineerName ?? '-'}</span>,
        defaultOn: false,
      },
      {
        key: 'prevYearAmount', label: `${year - 1}년`, type: 'number', align: 'right',
        accessor: (r) => r.prevYearAmount,
        render: (r) => (
          <span className="num text-slate-500">
            {money(r.prevYearAmount)}
          </span>
        ),
        defaultOn: true,
        ...maskedCol,
      },
      {
        key: 'currentYearAmount', label: `${year}년`, type: 'number', align: 'right',
        accessor: (r) => r.currentYearAmount,
        render: (r) => (
          <span className="num font-semibold">
            {money(r.currentYearAmount)}
          </span>
        ),
        defaultOn: true,
        ...maskedCol,
      },
      {
        key: 'invoicedAmount', label: '발행금액', type: 'number', align: 'right',
        accessor: (r) => r.invoicedAmount,
        render: (r) => (
          <span className="num text-blue-700">
            {money(r.invoicedAmount)}
          </span>
        ),
        defaultOn: true,
        ...maskedCol,
      },
      {
        key: 'subcontractAmount', label: '하도급', type: 'number', align: 'right',
        accessor: (r) => r.subcontractAmount,
        render: (r) => (
          <span className="num text-rose-600">
            {money(r.subcontractAmount)}
          </span>
        ),
        defaultOn: true,
        ...maskedCol,
      },
      {
        key: 'period', label: '계약기간', type: 'text', align: 'left',
        accessor: (r) => r.contractStart ?? '',
        // 정렬은 시작일 → 종료일 (시작일만 보면 같은 날 시작한 계약의 종료일이 뒤섞인다 · v2.271.0)
        sortAccessor: (r) => `${r.contractStart ?? ''}~${r.contractEnd ?? ''}`,
        render: (r) => (
          <span className="text-xs text-slate-500">
            {r.contractStart ?? '?'} ~ {r.contractEnd ?? '?'}
          </span>
        ),
        defaultOn: true,
      },
      {
        key: 'billingCycle', label: '청구주기', type: 'text', align: 'center',
        accessor: (r) => r.billingCycle ?? '',
        render: (r) => <span className="text-xs text-slate-600">{r.billingCycle ?? '-'}</span>,
        defaultOn: true,
      },
      {
        key: 'invoiceSchedule', label: '계산서 발행 약정', type: 'text', align: 'left',
        accessor: (r) => r.invoiceSchedule ?? '',
        render: (r) => <span className="text-xs text-slate-600">{r.invoiceSchedule ?? '-'}</span>,
        defaultOn: true,
      },
      {
        key: 'note', label: '비고', type: 'text', align: 'left',
        accessor: (r) => r.note ?? '',
        render: (r) => <span className="text-xs text-slate-500">{r.note ?? '-'}</span>,
        defaultOn: true,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [year, canEdit, isViewer],
  );

  // 📐 내 표 설정 prefs
  const prefsQ = useQuery({
    queryKey: ['preferences'],
    queryFn: prefsApi.getPreferences,
    staleTime: 5 * 60_000,
  });
  const patchPrefs = useMutation({
    mutationFn: (next: Partial<UserPreferences>) => prefsApi.patchPreferences(next),
    onSuccess: (newPrefs) => qc.setQueryData(['preferences'], newPrefs),
  });
  const t = useExcelModeTable({
    prefs: prefsQ.data,
    fullColumns: fullColumns as unknown as (Column<unknown> & { defaultOn: boolean })[],
    prefsKey: {
      excelMode: 'maintenanceTabExcelMode',
      columns: 'maintenanceTabColumns',
      columnsKnown: 'maintenanceTabColumnsKnown',
      order: 'maintenanceTabColumnOrder',
      widths: 'maintenanceTabColumnWidths',
    },
    patch: (next) => patchPrefs.mutate(next),
  });
  const visibleColumns = t.visibleColumns as unknown as MaintenanceColumn[];
  const [colMenuOpen, setColMenuOpen] = useState(false);

  // 📅 월별 발행일자 뷰 컬럼 — 계약현황에서 지금 보이는 컬럼을 기준으로,
  //   ① 비고 + 금액 3종(2025년·2026년·하도급) 제거(발행금액만 남김)
  //   ② '계산서 발행정보'(엑셀 열, contract_name 에 적재) 한 칸 추가
  //   ③ 그 뒤에 1~12월분 발행일 컬럼을 붙인다.
  //   왼쪽 식별 칸(상태·고객사·청구업체·사업자)은 앞쪽이라 위치·너비가 그대로 유지됨.
  const dateColumns = useMemo<MaintenanceColumn[]>(() => {
    const monthCols: MaintenanceColumn[] = Array.from({ length: 12 }, (_, i) => ({
      key: `m${i + 1}`,
      label: `${i + 1}월분`,
      type: 'text',
      align: 'center',
      nowrapHeader: true,
      accessor: (r) => r.monthlyInvoiceDates?.[i] ?? '',
      render: (r) => {
        const d = r.monthlyInvoiceDates?.[i] ?? null;
        // 🧾 그 달에 붙은 세금계산서 PDF (v2.262.0 · 봇 자동 매칭·수동 지정분만) — 줄 클릭(계약 창)과 따로 논다
        const pdfId = r.monthlyInvoicePdfs?.[i] ?? null;
        if (pdfId != null) {
          // ⚠칸 폭(70px · 안쪽 45px)에 「10/15」+🧾 가 안 들어간다(59px · 10-15 실측) → 날짜 자체를 누르게 하고
          //   🧾 는 칸 오른쪽 여백에 띄운다(absolute) — 12개 달 칸 폭이 그대로다.
          return (
            <button
              type="button"
              data-month-taxinv={i + 1}
              onClick={(e) => {
                e.stopPropagation();
                previewAttachment({ id: pdfId, filename: `${r.endUser} ${i + 1}월분 세금계산서.pdf` });
              }}
              className="relative whitespace-nowrap text-sky-700 underline decoration-dotted underline-offset-2 hover:text-sky-900"
              title={`${i + 1}월분 세금계산서 PDF 보기`}
            >
              {d ? fmtMd(d) : '·'}
              <span aria-hidden className="absolute left-full ml-px top-1/2 -translate-y-1/2 text-[11px] leading-none no-underline">🧾</span>
            </button>
          );
        }
        return (
          <span className={`whitespace-nowrap ${d ? 'text-slate-700' : 'text-slate-300'}`}>
            {d ? fmtMd(d) : '·'}
          </span>
        );
      },
      defaultOn: true,
    }));
    // '계산서 발행정보' — 운영 엑셀의 해당 열(품목명/발행 안내). contract_name 에 적재됨.
    //   내용 줄바꿈(\r) 보존해 여러 줄로 그대로 표시.
    const invoiceInfoCol: MaintenanceColumn = {
      key: 'invoiceInfo',
      label: '계산서 발행정보',
      type: 'text',
      align: 'left',
      accessor: (r) => r.contractName ?? '',
      render: (r) => {
        const v = (r.contractName ?? '').replace(/\r\n?/g, '\n').trim();
        return (
          <span className="text-xs text-slate-600 whitespace-pre-line">{v || '-'}</span>
        );
      },
      defaultOn: true,
    };
    // 날짜 뷰에서 뺄 컬럼 — 비고 + 금액 3종. 발행금액(invoicedAmount)만 남긴다.
    const HIDE = new Set(['note', 'prevYearAmount', 'currentYearAmount', 'subcontractAmount']);
    const base = visibleColumns.filter((c) => !HIDE.has(c.key));
    return [...base, invoiceInfoCol, ...monthCols];
  }, [visibleColumns]);

  // 통합검색 먼저 → 그 위에 머리글 정렬·🔍 필터 (영업진행·매입관리도 검색으로 받은 줄 위에 정렬·필터를 건다).
  //   필터 팝오버의 값 목록(baseRows)도 검색으로 좁힌 줄에서 뽑는다 — 영업진행과 같다.
  const searchedRows = useMemo(() => filterMaintRows(rows, search), [rows, search]);
  const visibleRows = useMemo(
    () => applyColState(searchedRows, dateView ? dateColumns : fullColumns, colState),
    [searchedRows, fullColumns, dateColumns, colState, dateView],
  );

  const totals = useMemo(() => {
    const t2 = { prev: 0, cur: 0, inv: 0 };
    for (const r of visibleRows) {
      t2.prev += r.prevYearAmount ?? 0;
      t2.cur += r.currentYearAmount ?? 0;
      t2.inv += r.invoicedAmount ?? 0;
    }
    return t2;
  }, [visibleRows]);

  if (rows.length === 0) {
    return (
      <div className="bg-white rounded border border-slate-200 p-12 text-center text-slate-400 text-sm">
        {year}년 계약기간에 해당하는 유지보수 계약이 없습니다.
      </div>
    );
  }

  const filterCount = Object.keys(colState.filters).length;

  // 현재 모드 컬럼 집합 — 계약현황(visibleColumns) 또는 월별 발행일자(dateColumns).
  const cols = dateView ? dateColumns : visibleColumns;
  const headerExcel = t.excelMode && !dateView; // 날짜 뷰에선 컬럼 이동/너비조절 비활성(정렬·필터는 유지)

  // 컬럼별 실제 적용 너비(px) — 사용자가 저장한 너비 우선, 없으면 모드별 기본값.
  const widthMap = dateView ? DATE_COL_WIDTH : DEFAULT_COL_WIDTH;
  const effWidth = (key: string) =>
    t.columnWidths?.[key] ?? widthMap[key] ?? FALLBACK_COL_WIDTH_PX;
  // table-fixed 전체 너비 = 보이는 컬럼 폭 합.
  const tableWidthPx = cols.reduce((a, c) => a + effWidth(c.key), 0);

  // 계약현황 뷰일 때 실제 헤더/각 행 높이를 측정해 보관. (월별 발행일자 뷰에서 같은 높이 적용)
  useLayoutEffect(() => {
    if (dateView) return; // 날짜 뷰에선 측정하지 않고, 계약현황에서 잰 값을 사용
    const table = tableRef.current;
    if (!table) return;
    const head = table.querySelector('thead tr');
    if (head) heightsRef.current.header = (head as HTMLElement).getBoundingClientRect().height;
    const trs = table.querySelectorAll<HTMLElement>('tbody tr[data-cid]');
    trs.forEach((tr) => {
      const id = Number(tr.dataset.cid);
      if (!Number.isNaN(id)) heightsRef.current.rows.set(id, tr.getBoundingClientRect().height);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateView, visibleRows, tableWidthPx]);

  return (
    <section className="bg-white rounded border border-slate-200">
      <div className="flex items-center justify-between p-4 border-b border-slate-200 flex-wrap gap-2">
        <h2 className="ui-section-title">
          {dateView ? `계산서별 발행일 (${year}년)` : `계약현황 (${year}년)`}
        </h2>
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <div className="text-right leading-tight">
            {/* 조회 전용은 합계도 가린다 — 합계에서 계약 금액이 거꾸로 드러난다 */}
            <div className="text-sm font-semibold">
              <span className="text-slate-700">연간 청구 ₩{isViewer ? MASK : fmtKrw(totals.cur)}</span>
              <span className="mx-1.5 text-slate-300 font-normal">·</span>
              <span className="text-blue-700">월 청구 ₩{isViewer ? MASK : fmtKrw(Math.round(totals.cur / 12))}</span>
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              전년 ₩{isViewer ? MASK : fmtKrw(totals.prev)} · 발행계 ₩{isViewer ? MASK : fmtKrw(totals.inv)}
            </div>
          </div>
          {/* 검색·정렬·필터는 두 보기에 다 걸리므로 초기화도 두 보기에 (v2.271.0 · 전엔 계약현황에서만 보였다) */}
          {(colState.sortKey || filterCount > 0 || search !== '') && (
            <ResetButton
              onClick={() => {
                colState.resetAll();
                onClearSearch();
              }}
              label={`↺ 필터·정렬 초기화${filterCount > 0 ? ` (${filterCount}컬럼)` : ''}`}
            />
          )}
          {!dateView && (
            <ExcelModeGroup
              enabled={t.excelMode}
              hasPrefs={t.hasPrefs}
              userName={user?.name ?? '사용자'}
              onChange={t.setExcelMode}
              onReset={t.handleReset}
            />
          )}
        </div>
      </div>
      {/* 컬럼 표시 토글 — 📐 내 표 설정 ON 일 때만. 모바일 hidden. */}
      {!dateView && t.excelMode && (
        <div className="hidden md:block px-4 py-2 border-b border-slate-100 text-xs bg-blue-50/30">
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => setColMenuOpen((v) => !v)}
              className="text-blue-600 hover:underline"
            >
              ⚙ 표시 컬럼 ({visibleColumns.length}/{t.fullAllKeys.length}{patchPrefs.isPending ? ' · 저장 중...' : ''})
              {colMenuOpen ? ' ▲' : ' ▼'}
            </button>
            <span className="text-xs text-slate-500">
              💡 컬럼 헤더 끌어 이동 / 우측 끝 끌어 너비 조정 / 헤더 ◀ ▶ 한 칸씩 이동
            </span>
          </div>
          {colMenuOpen && (
            <div className="mt-2 grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-1">
              {fullColumns.map((c) => {
                const on = t.activeColKeys.has(c.key);
                return (
                  <label
                    key={c.key}
                    className={`flex items-center gap-1.5 px-2 py-1 rounded cursor-pointer text-xs ${
                      on ? 'bg-blue-50 text-blue-800' : 'text-slate-500 hover:bg-slate-100'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={() => t.toggleColumn(c.key)}
                      disabled={patchPrefs.isPending}
                    />
                    <span>{c.toggleLabel ?? c.label}</span>
                  </label>
                );
              })}
            </div>
          )}
        </div>
      )}
      <div className="overflow-x-auto">
        <table
          ref={tableRef}
          className="text-sm border-collapse"
          style={{ tableLayout: 'fixed', width: '100%', minWidth: `${tableWidthPx}px` }}
        >
          <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10">
            <tr
              className="text-slate-700"
              style={
                dateView && heightsRef.current.header
                  ? { height: `${heightsRef.current.header}px` }
                  : undefined
              }
            >
              {cols.map((c, idx) => (
                <ColumnHeader
                  key={c.key}
                  column={c}
                  baseRows={searchedRows}
                  // 연쇄 필터 — applyColState 와 같은 컬럼 집합 (보기에 따라 달라진다)
                  columns={dateView ? dateColumns : fullColumns}
                  colState={colState}
                  excelMode={headerExcel}
                  onReorder={headerExcel ? t.handleReorder : undefined}
                  onMoveLeft={headerExcel && idx > 0 ? () => t.handleMoveLeft(c.key) : undefined}
                  onMoveRight={
                    headerExcel && idx < cols.length - 1
                      ? () => t.handleMoveRight(c.key)
                      : undefined
                  }
                  onResize={headerExcel ? t.handleResize : undefined}
                  widthPx={effWidth(c.key)}
                />
              ))}
              {/* 남는 가로 여백을 흡수하는 빈 칸 — 실제 컬럼 폭은 고정 유지(넓은 화면에서 표가 꽉 참). */}
              <th aria-hidden className="p-0" />
            </tr>
          </thead>
          <tbody>
            {visibleRows.length === 0 && (
              <tr>
                <td colSpan={cols.length + 1} className="text-center py-8 text-slate-400">
                  {search !== '' ? `「${search}」 검색·필터 조건에 맞는 계약이 없습니다.` : '필터 조건에 맞는 계약이 없습니다.'}
                </td>
              </tr>
            )}
            {visibleRows.map((row) => (
              <tr
                key={row.contractId}
                data-cid={row.contractId}
                onClick={() => onOpen(row.contractId)}
                className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                style={
                  dateView && heightsRef.current.rows.get(row.contractId)
                    ? { height: `${heightsRef.current.rows.get(row.contractId)}px` }
                    : undefined
                }
              >
                {cols.map((col) => {
                  const align =
                    col.align === 'right'
                      ? 'text-right'
                      : col.align === 'center'
                      ? 'text-center'
                      : 'text-left';
                  // 내용은 잘리지 않게 그대로(길면 여러 줄 줄바꿈 → 행 높이 늘어남). 상태 칸은 배지 그대로.
                  const content = col.render ? col.render(row) : String(col.accessor(row) ?? '');
                  return (
                    <td
                      key={col.key}
                      className={`px-2 py-1 border-b border-slate-200 align-top ${align}`}
                    >
                      {content}
                    </td>
                  );
                })}
                <td aria-hidden className="p-0" />
              </tr>
            ))}
            {visibleRows.length > 0 &&
              (dateView ? (
                <tr className="font-bold bg-amber-50">
                  <td colSpan={cols.length + 1} className="px-3 py-2 text-right text-slate-700">
                    합계 ({visibleRows.length}건) · {year}년 발행금액계{' '}
                    <span className="text-blue-800">{fmtKrw(totals.inv)}</span>
                  </td>
                </tr>
              ) : (
                <tr className="font-bold bg-amber-50">
                  <td
                    colSpan={cols.length + 1}
                    className="px-3 py-2 text-right text-slate-700"
                  >
                    합계 ({visibleRows.length}건) · {year - 1}년 {fmtKrw(totals.prev)} ·{' '}
                    {year}년 {fmtKrw(totals.cur)} · 발행계{' '}
                    <span className="text-blue-800">{fmtKrw(totals.inv)}</span>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-slate-500 px-4 py-2 border-t border-slate-100">
        {dateView
          ? '※ 각 칸 = 해당 월분 계산서의 실제 발행일(월/일). 빈칸 = 미발행. 행 클릭 시 계약 상세 + 월별 발행 현황. 컬럼 헤더 클릭으로 정렬, 호버 시 돋보기로 필터.'
          : '※ 모든 금액 부가세 별도. 행 클릭 시 계약 상세 + 월별 발행 현황. 컬럼 헤더 클릭으로 정렬, 호버 시 돋보기로 필터.'}
      </p>
    </section>
  );
}

// ─── 월별 발행내역 매트릭스 ─────────────────────────────

function MonthlyMatrix({
  rows,
  carry,
  year,
  onOpen,
}: {
  rows: MaintenanceMonthlyRow[];
  carry: MaintenancePrevCarryRow[];
  year: number;
  /** 행 클릭 → 계약 상세 (월별 발행 현황이 그 안에 있다, v2.79.0) */
  onOpen: (contractId: number) => void;
}) {
  // 조회 전용(viewer) — 서버가 금액을 null 로, 대신 issued(그 달에 계산서가 나갔나)를 보낸다 (v2.251.0)
  const masked = rows.some((r) => r.total == null);
  const monthTotals = useMemo(() => {
    const t = Array(12).fill(0);
    for (const r of rows) {
      r.months.forEach((v, i) => {
        t[i] += v ?? 0;
      });
    }
    return t;
  }, [rows]);
  const grandTotal = monthTotals.reduce((a, b) => a + b, 0);
  const carryTotal = carry.reduce((a, r) => a + r.amount, 0);

  if (rows.length === 0) {
    return (
      <div className="bg-white rounded border border-slate-200 p-12 text-center text-slate-400 text-sm">
        {year}년에 발행된 유지보수 인보이스가 없습니다.
      </div>
    );
  }

  return (
    <section className="bg-white rounded border border-slate-200 p-4">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <h2 className="ui-section-title">월별 발행합계 ({year}년)</h2>
          <p className="text-xs text-slate-500 mt-0.5">※ 계산서 <b>발행월(발행일)</b> 기준 집계 — 전년도 12월분이 올 1월에 발행되면 올해 1월에 잡힙니다. 행을 누르면 그 계약의 계산서별 발행일·금액이 나옵니다.</p>
        </div>
        <div className="text-xs text-slate-500">
          {rows.length}개 고객사 · 연간 합계 {masked ? MASK : fmtKrw(grandTotal)}원
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[13px] border border-slate-200">
          <thead className="text-white text-xs sticky top-0 z-10">
            <tr>
              <Th align="left">고객사</Th>
              <Th align="left">계약업체</Th>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((m) => (
                <Th key={m} align="right">{m}월</Th>
              ))}
              <Th align="right">합계</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.contractId}
                onClick={() => onOpen(r.contractId)}
                title="클릭 → 계약 상세 + 월별 발행 현황 (계산서별 발행일·금액)"
                className="border-t border-slate-100 cursor-pointer hover:bg-slate-50"
              >
                <td className="px-2 py-1.5 font-medium">{r.endUser || '-'}</td>
                <td className="px-2 py-1.5 text-xs text-slate-600">{r.contractCompany || '-'}</td>
                {r.months.map((v, i) => {
                  // 가려진 칸 — 계산서가 나간 달만 ●(금액 아닌 정보 · 계산서별 발행일 탭과 같은 수준)
                  if (v == null) {
                    const on = !!r.issued?.[i];
                    return (
                      <td key={i} className={`px-2 py-1.5 text-center ${on ? 'text-blue-600' : 'text-slate-300'}`} title={on ? '계산서 발행' : undefined}>
                        {on ? '●' : '·'}
                      </td>
                    );
                  }
                  return (
                    <td
                      key={i}
                      className={`px-2 py-1.5 text-right num ${v > 0 ? '' : 'text-slate-300'}`}
                    >
                      {v > 0 ? fmtKrw(v) : '·'}
                    </td>
                  );
                })}
                <td className="px-2 py-1.5 text-right num font-semibold">{r.total == null ? MASK : fmtKrw(r.total)}</td>
              </tr>
            ))}
            <tr className="font-bold bg-amber-50">
              <td colSpan={2} className="px-2 py-1.5 text-center">월별 합계</td>
              {monthTotals.map((v, i) => (
                <td key={i} className={`px-2 py-1.5 num ${masked ? 'text-center' : 'text-right'}`}>
                  {masked ? MASK : v > 0 ? fmtKrw(v) : '·'}
                </td>
              ))}
              <td className="px-2 py-1.5 text-right num text-blue-800">{masked ? MASK : fmtKrw(grandTotal)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {carry.length > 0 && (
        <div className="mt-4">
          <h3 className="ui-subsection-title mb-2">
            전년도 이월 ({carry.length}건 · 합계 {fmtKrw(carryTotal)}원)
          </h3>
          <table className="w-full text-[13px] border border-slate-200">
            <thead className="text-white text-xs sticky top-0 z-10">
              <tr>
                <Th align="left">고객사</Th>
                <Th align="left">계약업체</Th>
                <Th align="right">금액</Th>
              </tr>
            </thead>
            <tbody>
              {carry.map((r) => (
                <tr
                  key={`${r.endUser}|${r.contractCompany}`}
                  className="border-t border-slate-100"
                >
                  <td className="px-2 py-1.5 font-medium">{r.endUser}</td>
                  <td className="px-2 py-1.5 text-xs text-slate-600">{r.contractCompany}</td>
                  <td className="px-2 py-1.5 text-right num">{fmtKrw(r.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function Th({
  children,
  align = 'center',
}: {
  children: ReactNode;
  align?: 'left' | 'center' | 'right';
}) {
  const cls =
    align === 'left' ? 'text-left' : align === 'right' ? 'text-right' : 'text-center';
  return (
    <th
      className={`px-2 py-1.5 ${cls}`}
      style={{ background: '#334155', color: '#fff' }}
    >
      {children}
    </th>
  );
}
